import axiosInstance from '../utils/axiosInstance';

// DRF list endpoints are paginated ({count, results}); unwrap to the array.
const list = (data) => (data && Array.isArray(data.results) ? data.results : data);

const get = (url, params) => axiosInstance.get(url, { params }).then((r) => r.data);
const post = (url, body) => axiosInstance.post(url, body).then((r) => r.data);
const patch = (url, body) => axiosInstance.patch(url, body).then((r) => r.data);
const del = (url) => axiosInstance.delete(url).then((r) => r.data);

export const api = {
  // --- Sources ---------------------------------------------------------
  sources: {
    list: () => get('api/sources/').then(list),
    get: (id) => get(`api/sources/${id}/`),
    create: (body) => post('api/sources/', body),
    update: (id, body) => patch(`api/sources/${id}/`, body),
    remove: (id) => del(`api/sources/${id}/`),
    sync: (id) => post(`api/sources/${id}/sync/`, {}),
    fields: (id) => get(`api/sources/${id}/fields/`),
    query: (id) => get(`api/sources/${id}/query/`),
    preview: (id, params) => get(`api/sources/${id}/preview/`, params),
    liveCount: (id) => get(`api/sources/${id}/live_count/`),
    runs: (id) => get(`api/sources/${id}/runs/`),
  },

  // --- Catalog items ---------------------------------------------------
  items: {
    list: (params) => get('api/items/', params),
    fields: (params) => get('api/items/fields/', params),
  },

  // --- Feeds -----------------------------------------------------------
  feeds: {
    list: () => get('api/feeds/').then(list),
    get: (id) => get(`api/feeds/${id}/`),
    create: (body) => post('api/feeds/', body),
    update: (id, body) => patch(`api/feeds/${id}/`, body),
    remove: (id) => del(`api/feeds/${id}/`),
    preview: (id, params = {}) => get(`api/feeds/${id}/preview/`, { limit: 25, ...params }),
    generate: (id) => post(`api/feeds/${id}/generate/`, {}),
    bulkGenerate: (feedIds) => post('api/feeds/bulk_generate/', { feed_ids: feedIds }),
    runs: (id) => get(`api/feeds/${id}/runs/`),
    clone: (id, body) => post(`api/feeds/${id}/clone/`, body),
    spawn: (id, body) => post(`api/feeds/${id}/spawn/`, body),
    compare: (id, body) => post(`api/feeds/${id}/compare/`, body || {}),
    comparisons: (id) => get(`api/feeds/${id}/comparisons/`),
    mismatches: (id, field, params = {}) => get(`api/feeds/${id}/mismatches/`, { field, ...params }),
    issueRecords: (id, params) => get(`api/feeds/${id}/issue_records/`, params),
    // Base (PIM) products that did NOT make it into the feed, each with a reason:
    // {base_total, output_total, excluded_total, by_reason: {<reason>: count},
    //  sample: [{id, title, image, reason}]}. Cached ~10 min; {refresh: 1} recomputes.
    excluded: (id, params) => get(`api/feeds/${id}/excluded/`, params),
  },

  // --- Pipeline runs (daily sync+generate history) ---------------------
  pipelineRuns: {
    list: () => get('api/pipeline-runs/').then(list),
    run: (body) => post('api/pipeline-runs/run/', body || {}),
  },

  // --- Mappings & rules ------------------------------------------------
  mappings: {
    create: (body) => post('api/mappings/', body),
    update: (id, body) => patch(`api/mappings/${id}/`, body),
    remove: (id) => del(`api/mappings/${id}/`),
  },
  rules: {
    create: (body) => post('api/rules/', body),
    update: (id, body) => patch(`api/rules/${id}/`, body),
    remove: (id) => del(`api/rules/${id}/`),
  },

  // --- Channels & presets ----------------------------------------------
  channels: {
    list: () => get('api/channels/'),
  },
  rulePresets: {
    list: () => get('api/rule-presets/'),
  },

  // --- Master rules (global catalog rules, grouped) --------------------
  masterRuleGroups: {
    list: () => get('api/master-rule-groups/').then(list),
    get: (id) => get(`api/master-rule-groups/${id}/`),
    create: (body) => post('api/master-rule-groups/', body),
    update: (id, body) => patch(`api/master-rule-groups/${id}/`, body),
    remove: (id) => del(`api/master-rule-groups/${id}/`),
    // Final-volume distribution after the whole cascade (last match wins):
    // {total, per_rule: {<rule_id>: count}, per_category: {<path>: count}}.
    // Cached ~10 min server-side; pass {refresh: 1} to recompute.
    distribution: (id, params) => get(`api/master-rule-groups/${id}/distribution/`, params),
  },
  masterRules: {
    create: (body) => post('api/master-rules/', body),
    update: (id, body) => patch(`api/master-rules/${id}/`, body),
    remove: (id) => del(`api/master-rules/${id}/`),
    // Which catalog items match this rule's condition (count + sample).
    affected: (id, params) => get(`api/master-rules/${id}/affected/`, params),
  },
};

export default api;
